/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OpenAPIHousien } from '@/services/requests/core/OpenAPI';
import { request as __request } from '@/services/requests/core/request';

/** The transactions with their own list; the form maps Venda/Aluguel here. */
export type PropertySettingsTransaction = 'sale' | 'rent';

export type PropertyRequiredFields = Record<PropertySettingsTransaction, string[]>;

export interface PropertySettings {
  agency_id: string;
  required_fields: PropertyRequiredFields;
  /** The floor the form always requires; shown, never the agency's to change. */
  locked_fields: string[];
  /** What the agency may add on top of the floor, in display order. */
  configurable_fields: string[];
  updated_at: string;
  updated_by: string;
}

/** The form's transaction values, mapped to the settings' keys. */
export const TRANSACTION_TO_SETTINGS_KEY: Record<string, PropertySettingsTransaction> = {
  Venda: 'sale',
  Aluguel: 'rent',
};

/** React Query key for an agency's property settings. */
export const propertySettingsKey = (agencyId: string) => ['property-settings', agencyId] as const;

/** Reads the agency's property registration settings. */
function readPropertySettings(agencyId: string): Promise<PropertySettings> {
  return __request<PropertySettings>(OpenAPIHousien, {
    method: 'GET',
    url: '/agencies/{agencyId}/property-settings',
    path: { agencyId },
  });
}

/** Replaces the required-field list of each transaction sent. */
function writeRequiredFields(
  agencyId: string,
  requiredFields: Partial<PropertyRequiredFields>,
): Promise<PropertySettings> {
  return __request<PropertySettings>(OpenAPIHousien, {
    method: 'PUT',
    url: '/agencies/{agencyId}/property-settings',
    path: { agencyId },
    body: { required_fields: requiredFields },
  });
}

/**
 * Which fields a broker must fill, per transaction, before the property form
 * saves. Read by the settings screen and by the form itself, through the same
 * query key, so a save in one is what the other applies next.
 *
 * @param agencyId - The owning agency.
 * @returns The settings (null while loading or unreadable), load state and the write.
 */
export function usePropertySettings(agencyId: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: propertySettingsKey(agencyId),
    queryFn: () => readPropertySettings(agencyId),
    enabled: Boolean(agencyId),
  });

  const mutation = useMutation({
    mutationFn: (requiredFields: Partial<PropertyRequiredFields>) =>
      writeRequiredFields(agencyId, requiredFields),
    onSuccess: (saved) => queryClient.setQueryData(propertySettingsKey(agencyId), saved),
  });

  return {
    settings: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    saveRequiredFields: (requiredFields: Partial<PropertyRequiredFields>) =>
      mutation.mutateAsync(requiredFields),
  };
}
